import { EllipsisVertical, MessageCircleQuestion, X } from "lucide-react";
import { showsEstimate } from "./accountCurrency";
import { useAccountCurrency } from "./CurrencySelector";
import { projectName, projectStructureType } from "./projectText";
import { lazy, type ReactNode, Suspense } from "react";
import {
  FilterLiveForm,
  InfiniteListBase,
  useListContext,
  useLocaleState,
  useTranslate,
} from "ra-core";
import { Link, matchPath, useLocation } from "react-router";
import { SearchInput } from "@/components/admin";
import { DataTable } from "@/components/admin/data-table";
import { List } from "@/components/admin/list";
import { ListPagination } from "@/components/admin/list-pagination";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useIsMobile } from "@/hooks/use-mobile";

import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import { TopToolbar } from "../layout/TopToolbar";
import { InfinitePagination } from "../misc/InfinitePagination";
import type { Project } from "../types";
import { AlertBell } from "./AlertList";
import { ProjectExportButton } from "./ProjectExportButton";
import { MatchPairsButton } from "./MatchPairList";
import { PhaseBadge } from "./PhaseBadge";
import { ProjectPanel, useOpenProject } from "./ProjectPanel";
import { ProjectListFilter } from "./ProjectListFilter";
import { QuestionsButton } from "./QuestionsPage";
import { activeFilterChips, type FilterChip, withoutChip } from "./filterChips";
import { isStaleNews } from "./newsAge";
import { SaveSearchButton } from "./SavedSearches";
import { HiddenProjectsNote } from "./Teaser";
import { useListView } from "./projectMapView";
import { MapFallback, ViewToggle } from "./ViewToggle";
import { ScoreStamp } from "./ScoreCard";
import { useScreenTip, type TipSpec } from "../onboarding/useScreenTip";
import {
  categoryKey,
  countryName,
  formatBudget,
  formatDay,
  HIDE_OTHER,
  phaseKey,
  sourceTypeKey,
} from "./projectUtils";
import { HelpTip } from "./HelpTip";

export const ProjectList = () =>
  useIsMobile() ? <ProjectListMobile /> : <ProjectListDesktop />;

const TIPS: TipSpec[] = [
  { target: "score", key: "projects.score" },
  { target: "filters", key: "projects.filters" },
];

// La carte et MapLibre ne sont chargés qu'à la première ouverture de la vue carte.
const ProjectMap = lazy(() => import("./ProjectMap"));

// Sur ordinateur, la fiche s'ouvre en panneau à côté de la liste (route /projects/:id/show) : on garde sa place.
const ProjectListDesktop = () => {
  const location = useLocation();
  const selected = matchPath("/projects/:id/show/*", location.pathname)?.params
    .id;
  const [view, setView] = useListView("projects");
  return (
    <List
      title={false}
      perPage={25}
      sort={{ field: "score", order: "DESC" }}
      // La catégorie « autre » (routes, aménagements) est masquée tant que l'utilisateur ne la demande pas.
      filterDefaultValues={HIDE_OTHER}
      actions={
        <TopToolbar>
          <QuestionsButton />
          <MatchPairsButton />
          <SaveSearchButton />
          <ProjectExportButton />
        </TopToolbar>
      }
      pagination={
        view === "map" ? null : (
          <ListPagination rowsPerPageOptions={[25, 50, 100]} />
        )
      }
    >
      <div className="w-full flex flex-row items-start gap-6">
        {/* Panneau ouvert : la colonne des filtres laisse sa place aux colonnes de la liste, les filtres restent en étiquettes. */}
        {!selected && <ProjectListFilter />}
        <div className="flex-1 min-w-0">
          <ListHeader
            panel={!!selected}
            toggle={<ViewToggle view={view} onChange={setView} />}
            onShowList={view === "map" ? () => setView("list") : undefined}
          />
          <HiddenProjectsNote className="mb-3" />
          {view === "map" ? (
            <Suspense fallback={<MapFallback />}>
              <ProjectMap selected={selected} />
            </Suspense>
          ) : (
            <ProjectTable selected={selected} />
          )}
        </div>
        {selected && <ProjectPanel id={selected} />}
      </div>
    </List>
  );
};

/** Panneau ouvert : la liste garde ses colonnes, sauf la catégorie ; le sous-titre tient sur une ligne. */
const ProjectTable = ({ selected }: { selected?: string }) => {
  const { currency, estimate } = useAccountCurrency();
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const open = useOpenProject();
  const full = !selected;
  const { data } = useListContext<Project>();
  // Panneau ouvert : la bulle de la fiche passe seule (Driver n'a qu'une visite active à la fois).
  useScreenTip("tip:projects", TIPS, !!data?.length && !selected);
  return (
    <DataTable<Project>
      rowClick={(id) => {
        open(id);
        return false;
      }}
      rowClassName={(record) =>
        String(record.id) === selected
          ? "project-row-selected bg-signal-soft hover:bg-signal-soft dark:bg-sidebar-accent dark:hover:bg-sidebar-accent"
          : undefined
      }
      bulkActionButtons={false}
    >
      <DataTable.Col
        source="score"
        // Le score mêle règles et IA : son tampon une fois, dans l'en-tête de la colonne.
        label={
          <span className="inline-flex items-center gap-1.5" data-tour="score">
            {translate("resources.projects.fields.score")}
            <ScoreStamp />
            <HelpTip topic="score" className="-ml-1" />
          </span>
        }
        render={(record) => (
          <div className="flex flex-col">
            <span className="font-semibold">{record.score ?? "–"}</span>
            {record.to_specify && <ToSpecify />}
          </div>
        )}
      />
      <DataTable.Col
        source="name"
        render={(record: Project) => (
          <div
            className={`${full ? "min-w-56" : "min-w-44"} max-w-md whitespace-normal`}
          >
            <div className="font-medium">{projectName(record, locale)}</div>
            <div
              className={`text-xs text-muted-foreground ${full ? "line-clamp-2" : "line-clamp-1"}`}
            >
              {[projectStructureType(record, locale), record.owner_names]
                .filter(Boolean)
                .join(" · ")}
            </div>
          </div>
        )}
      />
      {full && (
        <DataTable.Col
          source="category"
          render={(record) => translate(categoryKey(record.category))}
        />
      )}
      <DataTable.Col
        source="country"
        render={(record) => (
          <div className="min-w-24 whitespace-normal">
            {[record.city, countryName(record.country, locale)]
              .filter(Boolean)
              .join(", ")}
          </div>
        )}
      />
      <DataTable.Col
        source="phase"
        render={(record) => <PhaseBadge phase={record.phase} />}
      />
      <DataTable.Col
        // Tri sur le montant en euros (estimation) ; la cellule garde la devise de la source.
        source="budget_eur"
        label={
          <span className="inline-flex items-center gap-0.5">
            {translate("resources.projects.fields.budget_amount")}
            <HelpTip topic="amount" />
          </span>
        }
        render={(record) => (
          <div className="whitespace-nowrap tabular-nums">
            <div>{formatBudget(record as Project, locale)}</div>
            {showsEstimate(record as Project, currency) && (
              <div className="text-xs text-muted-foreground">
                {estimate(record.budget_eur)}
              </div>
            )}
          </div>
        )}
      />
      <DataTable.Col
        source="last_news_at"
        label={
          <span className="inline-flex items-center gap-0.5">
            {translate("resources.projects.fields.last_news_at")}
            <HelpTip topic="sources" />
          </span>
        }
        render={(record) => (
          <SourceCell project={record as Project} compact={!full} />
        )}
      />
    </DataTable>
  );
};

/** En-tête de la liste : nombre de fiches et filtres actifs, chacun retirable ; la recherche y vient quand le panneau est ouvert. */
const ListHeader = ({
  panel,
  toggle,
  onShowList,
}: {
  panel: boolean;
  toggle: ReactNode;
  // Vue carte : lien qui repasse en liste, où toutes les fiches se parcourent.
  onShowList?: () => void;
}) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const { total, filterValues, setFilters } = useListContext<Project>();
  const chips = activeFilterChips(filterValues ?? {}).filter(
    // La recherche a son propre champ, pas d'étiquette.
    (chip) => chip.kind !== "q",
  );
  const t = (key: string, options?: object) =>
    translate(`resources.projects.list_header.${key}`, options);
  const date = (value: string) =>
    new Date(value).toLocaleDateString(locale ?? "fr");
  const millions = (value: string) =>
    Number(value).toLocaleString(locale ?? "fr");
  const one = (chip: FilterChip, value: string) =>
    chip.kind === "phase"
      ? translate(phaseKey(value), { _: value })
      : chip.kind === "category"
        ? translate(categoryKey(value))
        : chip.kind === "source_type"
          ? translate(sourceTypeKey(value))
          : countryName(value, locale);
  const chipLabel = (chip: FilterChip) => {
    const [value] = chip.values;
    switch (chip.kind) {
      case "phase":
      case "category":
      case "source_type":
      case "country":
        return chip.values.map((v) => one(chip, v)).join(", ");
      case "hide_other":
        return t("hide_other");
      case "min_score":
        return t("min_score", { value });
      case "min_eur":
        return t("min_eur", { value: millions(value) });
      case "min_eur_or_unknown":
        return t("min_eur_or_unknown", { value: millions(value) });
      case "max_eur":
        return t("max_eur", { value: millions(value) });
      case "created_after":
        return t("created_after", { date: date(value) });
      case "created_before":
        return t("created_before", { date: date(value) });
      case "news_after":
        return t("news_after", { date: date(value) });
      case "to_specify":
        return t("to_specify");
      default:
        return t("other");
    }
  };
  return (
    <div className="mb-3 flex min-h-9 flex-wrap items-center gap-2">
      <h1 className="text-lg font-bold">
        {translate("resources.projects.name", { smart_count: 2 })}
      </h1>
      {total != null && (
        <span className="mr-2 text-sm tabular-nums text-muted-foreground">
          {translate("resources.projects.list_header.count", {
            smart_count: total,
          })}
        </span>
      )}
      {onShowList && (
        <button
          type="button"
          onClick={onShowList}
          className="mr-2 text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground"
        >
          {t("show_list")}
        </button>
      )}
      {panel && (
        <FilterLiveForm>
          <SearchInput source="q" className="w-52" />
        </FilterLiveForm>
      )}
      {panel &&
        chips.map((chip) => {
          const label = chipLabel(chip);
          return (
            <span
              key={chip.key}
              className="inline-flex h-7 items-center gap-1 rounded-[3px] border border-border bg-card pl-2 text-xs font-medium"
            >
              {label}
              <button
                type="button"
                aria-label={translate("resources.projects.list_header.remove", {
                  label,
                })}
                className="flex size-7 items-center justify-center text-muted-foreground hover:text-foreground"
                onClick={() => setFilters(withoutChip(filterValues, chip.key))}
              >
                <X className="size-3.5" />
              </button>
            </span>
          );
        })}
      <div className="ml-auto">{toggle}</div>
    </div>
  );
};

/** Types des sources de la fiche, date de la dernière nouvelle (en orange au-delà de 12 mois) et nombre de documents.
 * Le nom et le lien de chaque document sont dans la section « Sources » de la fiche. */
const SourceCell = ({
  project,
  compact,
}: {
  project: Project;
  compact?: boolean;
}) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  // À côté du panneau, la date seule : les types sont dans la fiche.
  if (compact) return <LastNews project={project} locale={locale} />;
  return (
    <div className="text-xs">
      {project.source_types?.length ? (
        <div>
          {project.source_types
            .map((type) => translate(sourceTypeKey(type)))
            .join(" + ")}
        </div>
      ) : null}
      <LastNews project={project} locale={locale} />
      {(project.sources_count ?? 0) > 1 && (
        <div className="text-muted-foreground">
          {translate("resources.projects.documents_count", {
            smart_count: project.sources_count,
          })}
        </div>
      )}
    </div>
  );
};

const LastNews = ({ project, locale }: { project: Project; locale: string }) =>
  project.last_news_at ? (
    <div
      className={
        isStaleNews(project.last_news_at)
          ? "text-inferred-ink"
          : "text-muted-foreground"
      }
    >
      {formatDay(project.last_news_at, locale)}
    </div>
  ) : null;

const MobileMoreMenu = () => {
  const translate = useTranslate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="rounded-full"
          aria-label={translate("resources.projects.list_header.more")}
        >
          <EllipsisVertical className="size-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem asChild>
          <Link to="/questions">
            <MessageCircleQuestion />
            {translate("resources.projects.questions.title")}
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Sur téléphone : une carte par fiche, chargée au fil du défilement, filtres dans un panneau.
const ProjectListMobile = () => {
  const [view, setView] = useListView("projects");
  return (
    <InfiniteListBase
      perPage={25}
      sort={{ field: "score", order: "DESC" }}
      filterDefaultValues={HIDE_OTHER}
    >
      <MobileHeader>
        <ProjectListFilter />
        {/* Questions dans un menu : la recherche garde la largeur de son texte. */}
        <MobileMoreMenu />
        <SaveSearchButton iconOnly />
        <AlertBell />
      </MobileHeader>
      <MobileContent>
        {view === "map" ? (
          <Suspense fallback={<MapFallback />}>
            <ProjectMap mobile />
          </Suspense>
        ) : (
          <>
            <HiddenProjectsNote className="mb-2 px-1" />
            <ProjectCards />
            <div className="flex justify-center">
              <InfinitePagination />
            </div>
          </>
        )}
        {/* Bascule flottante au-dessus de la barre du bas, comme les applications de cartes. */}
        <div className="fixed inset-x-0 bottom-20 z-20 flex justify-center pointer-events-none">
          <div className="pointer-events-auto">
            <ViewToggle view={view} onChange={setView} floating />
          </div>
        </div>
      </MobileContent>
    </InfiniteListBase>
  );
};

const ProjectCards = () => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const { currency, estimate } = useAccountCurrency();
  const { data, isPending } = useListContext<Project>();
  useScreenTip("tip:projects", TIPS, !!data?.length);
  if (isPending) return null;
  if (!data?.length) {
    return (
      <p className="text-sm text-muted-foreground py-8 text-center">
        {translate("ra.navigation.no_results", { name: "" })}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      {data.map((record, index) => (
        <Link key={record.id} to={`/projects/${record.id}/show`}>
          <Card className="p-3 gap-1.5 active:bg-muted">
            <div className="flex items-start gap-3">
              <span className="flex flex-col w-8 shrink-0">
                <span
                  className="text-lg font-semibold tabular-nums"
                  data-tour={index === 0 ? "score" : undefined}
                >
                  {record.score ?? "–"}
                </span>
                {record.to_specify && <ToSpecify />}
              </span>
              <div className="min-w-0 flex-1">
                <div className="font-medium leading-snug line-clamp-3">
                  {projectName(record, locale)}
                </div>
                {record.owner_names && (
                  <div className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                    {record.owner_names}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pl-11 text-xs text-muted-foreground">
              <PhaseBadge phase={record.phase} />
              <span>{translate(categoryKey(record.category))}</span>
              <span>
                {[record.city, countryName(record.country, locale)]
                  .filter(Boolean)
                  .join(", ")}
              </span>
              <span>
                {formatBudget(record as Project, locale)}
                {showsEstimate(record, currency) &&
                  ` (${estimate(record.budget_eur)})`}
              </span>
              <LastNews project={record} locale={locale} />
            </div>
          </Card>
        </Link>
      ))}
    </div>
  );
};

/** Fiche sans pays, ou sans ville ni société : score plafonné en attendant une source plus précise. */
const ToSpecify = () => {
  const translate = useTranslate();
  return (
    <span className="text-[11px] leading-tight text-inferred-ink">
      {translate("resources.projects.to_specify")}
    </span>
  );
};
